// src/pages/dashboard/branch/analytics/SalesAnalyticsTab.tsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
    FiDollarSign,
    FiShoppingCart,
    FiCpu,
    FiPackage,
    FiTrendingUp,
    FiCalendar,
} from "react-icons/fi";
import Card from "../../../../components/ui/Card";
import { analyticsService } from "../../../../services/analyticsService";
import {
    GetAllBranchSalesResponse,
    GetItemPerformanceResponse,
    ItemPerformanceItem,
} from "../../../../types/analytics";
import { useCachedMenuItems, useCachedCategories } from "../../../../hooks/useCachedData";
import { useBranch } from "../../../../context/BranchContext";
import { useAnalyticsContext, convertBranchStartDateToDate } from "../../../../context/AnalyticsContext";
import { MenuItem } from "../../../../utils/supabaseClient";
import { formatPriceWithCurrency } from "../../../../utils/formatters";

// Import our new components
import {
    DateRangePicker,
    QuickDatePresets,
    getPresetDates,
    StatsCard,
    SalesChart,
    ItemBreakdownTable,
    ItemSelector,
    PerformanceComparisonChart,
    ItemPerformanceTable,
} from "./components";
import type { DatePreset } from "./components";

interface SalesAnalyticsTabProps {
    branchId: string;
}

// ========== Utility Functions ==========
/**
 * Get the end of day for a given date (23:59:59.999)
 * This ensures all date queries include the entire end date
 */
function getEndOfDay(date: Date): Date {
    const endDate = new Date(date);
    endDate.setHours(23, 59, 59, 999);
    return endDate;
}

const SalesAnalyticsTab: React.FC<SalesAnalyticsTabProps> = ({ branchId }) => {
    const { t, i18n } = useTranslation();
    const { selectedCurrency } = useBranch();
    const { branchStartDate: contextBranchStartDate } = useAnalyticsContext();

    const locale = i18n?.language || "en-US";
    const currencyCode = selectedCurrency?.iso || "USD";
    const currencySymbol = selectedCurrency?.symbol || currencyCode;
    const formatCurrencyValue = useCallback(
        (amount: number) => formatPriceWithCurrency(amount, locale, selectedCurrency),
        [locale, selectedCurrency]
    );

    // Calculate minimum allowed date based on branch creation date
    const minDateLimit = convertBranchStartDateToDate(contextBranchStartDate) || undefined;

    // ========== Branch Sales State ==========
    const [salesLoading, setSalesLoading] = useState(true);
    const [salesData, setSalesData] = useState<GetAllBranchSalesResponse | null>(null);
    const [salesError, setSalesError] = useState<string | null>(null);

    // Date state for branch overview (using Date objects)
    const [branchStartDate, setBranchStartDate] = useState<Date>(() => {
        // Default to last 7 days, but not before branch creation date
        const branchDate = convertBranchStartDateToDate(contextBranchStartDate);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        
        // If branch was created within last 7 days, use branch creation date instead
        if (branchDate && branchDate > sevenDaysAgo) {
            return branchDate;
        }
        return sevenDaysAgo;
    });
    const [branchEndDate, setBranchEndDate] = useState<Date>(new Date());
    const [activePreset, setActivePreset] = useState<DatePreset | null>("last7days");

    // ========== Item Performance State ==========
    const [performanceLoading, setPerformanceLoading] = useState(true);
    const [performanceData, setPerformanceData] = useState<GetItemPerformanceResponse | null>(null);
    const [performanceError, setPerformanceError] = useState<string | null>(null);
    const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);

    // Date state for item performance
    const [perfStartDate, setPerfStartDate] = useState<Date>(() => {
        // Use branch creation date if available, otherwise use 6 months back
        const startDate = convertBranchStartDateToDate(contextBranchStartDate);
        if (startDate) {
            return startDate;
        }
        const date = new Date();
        date.setMonth(date.getMonth() - 6);
        return date;
    });
    const [perfEndDate, setPerfEndDate] = useState<Date>(new Date());

    // ========== Cached Data ==========
    const { menuItems: cachedMenuItems } = useCachedMenuItems({ branchId });
    const { categories: cachedCategories } = useCachedCategories({ branchId });

    // Create menu items lookup map
    const menuItemsMap = useMemo(() => {
        const map = new Map<string, MenuItem>();
        cachedMenuItems.forEach((item) => map.set(item.id, item));
        return map;
    }, [cachedMenuItems]);

    // ========== Date Handlers ==========
    const handlePresetSelect = useCallback((preset: DatePreset) => {
        setActivePreset(preset);
        const { start, end } = getPresetDates(preset);
        // Ensure start date is not before branch creation date
        const minDate = convertBranchStartDateToDate(contextBranchStartDate);
        const validStart = minDate && start < minDate ? minDate : start;
        setBranchStartDate(validStart);
        setBranchEndDate(end);
    }, [contextBranchStartDate]);

    const handleBranchStartDateChange = useCallback((date: Date | null) => {
        if (date) {
            // Ensure start date is not before branch creation date
            const minDate = convertBranchStartDateToDate(contextBranchStartDate);
            if (minDate && date < minDate) {
                // Alert user that they cannot select a date before branch creation
                console.warn(`Cannot select a date before branch creation date: ${minDate.toLocaleDateString()}`);
                return;
            }
            setBranchStartDate(date);
            setActivePreset(null); // Clear preset when manually changing dates
        }
    }, [contextBranchStartDate]);

    const handleBranchEndDateChange = useCallback((date: Date | null) => {
        if (date) {
            // Ensure end date is not before branch creation date
            const minDate = convertBranchStartDateToDate(contextBranchStartDate);
            if (minDate && date < minDate) {
                console.warn(`Cannot select a date before branch creation date: ${minDate.toLocaleDateString()}`);
                return;
            }
            setBranchEndDate(date);
            setActivePreset(null);
        }
    }, [contextBranchStartDate]);

    // ========== Item Selection Handlers ==========
    const handleItemSelect = useCallback((itemId: string) => {
        setSelectedItemIds((prev) => {
            if (prev.includes(itemId) || prev.length >= 5) return prev;
            return [...prev, itemId];
        });
    }, []);

    const handleItemRemove = useCallback((itemId: string) => {
        setSelectedItemIds((prev) => prev.filter((id) => id !== itemId));
    }, []);

    // ========== Fetch Branch Sales ==========
    useEffect(() => {
        const fetchBranchSales = async () => {
            setSalesLoading(true);
            setSalesError(null);

            try {
                const response = await analyticsService.getAllBranchSales({
                    p_branch_id: branchId,
                    p_start_date: branchStartDate.toISOString().split("T")[0],
                    p_end_date: getEndOfDay(branchEndDate).toISOString().split("T")[0],
                });
                setSalesData(response);
            } catch (err) {
                console.error("Failed to fetch branch sales:", err);
                setSalesError(t("analytics.failedLoadSalesData", "Failed to load sales data. Please try again."));
            } finally {
                setSalesLoading(false);
            }
        };

        fetchBranchSales();
    }, [branchId, branchStartDate, branchEndDate]);

    // ========== Fetch Item Performance ==========
    useEffect(() => {
        const fetchPerformanceData = async () => {
            if (selectedItemIds.length === 0) {
                setPerformanceLoading(false);
                return;
            }

            setPerformanceLoading(true);
            setPerformanceError(null);

            try {
                const response = await analyticsService.getItemPerformance({
                    branch_id: branchId,
                    item_ids: selectedItemIds,
                    date_start: perfStartDate.toISOString().split("T")[0],
                    date_end: getEndOfDay(perfEndDate).toISOString().split("T")[0],
                });
                setPerformanceData(response);
            } catch (err) {
                console.error("Failed to fetch performance data:", err);
                setPerformanceError(t("analytics.failedLoadPerformanceData", "Failed to load performance data. Please try again."));
            } finally {
                setPerformanceLoading(false);
            }
        };

        fetchPerformanceData();
    }, [branchId, selectedItemIds, perfStartDate, perfEndDate]);

    // ========== Computed Data ==========

    // Branch sales totals - prefer using branch_sales_summary if available
    const branchTotals = useMemo(() => {
        if (!salesData) {
            return { totalSales: 0, totalOrders: 0, aiSales: 0, aiOrders: 0, regularSales: 0, regularOrders: 0 };
        }

        // Use branch_sales_summary for totals if available
        const branchSummary = salesData.branch_sales_summary?.data;
        let totalSales = 0;
        let totalOrders = 0;

        if (branchSummary?.overall_summary) {
            totalSales = branchSummary.overall_summary.grand_total_sales;
            totalOrders = branchSummary.overall_summary.grand_total_orders;
        } else if (branchSummary?.data) {
            totalSales = branchSummary.data.reduce((sum, day) => sum + (day.total_sales || 0), 0);
            totalOrders = branchSummary.data.reduce((sum, day) => sum + (day.total_orders || 0), 0);
        }

        // Calculate AI sales from ai_sales_summary
        let aiSales = 0;
        let aiOrders = 0;
        if (salesData.ai_sales_summary?.data?.data) {
            salesData.ai_sales_summary.data.data.forEach((item) => {
                if (item.summary) {
                    aiSales += item.summary.total_sales || 0;
                    aiOrders += item.summary.total_orders || 0;
                } else {
                    item.sales_detail.forEach((detail) => {
                        aiSales += detail.total_price || 0;
                        aiOrders += detail.order_quantity || 0;
                    });
                }
            });
        }

        return {
            totalSales,
            totalOrders,
            aiSales,
            aiOrders,
            regularSales: totalSales - aiSales,
            regularOrders: totalOrders - aiOrders,
        };
    }, [salesData]);

    // Branch chart data
    const branchChartData = useMemo(() => {
        if (!salesData?.branch_sales_summary?.data?.data) return [];

        return salesData.branch_sales_summary.data.data
            .map((day) => ({
                date: new Date(day.day).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                }),
                sales: day.total_sales || 0,
                orders: day.total_orders || 0,
            }))
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }, [salesData]);

    // Items breakdown data
    const itemsBreakdownData = useMemo(() => {
        if (!salesData?.item_sales_summary?.data?.data) return [];

        const aiMap = new Map(
            salesData.ai_sales_summary?.data?.data?.map((item) => [item.item_id, item]) || []
        );

        return salesData.item_sales_summary.data.data.map((item) => {
            const menuItem = menuItemsMap.get(item.item_id);
            const category = cachedCategories.find((cat) => cat.id === menuItem?.category_id);
            const aiItem = aiMap.get(item.item_id);

            // Calculate totals
            let orders = 0;
            let revenue = 0;
            if (item.summary) {
                orders = item.summary.total_orders;
                revenue = item.summary.total_sales;
            } else {
                item.sales_detail.forEach((detail) => {
                    orders += detail.order_quantity || 0;
                    revenue += detail.total_price || 0;
                });
            }

            let aiOrders = 0;
            let aiRevenue = 0;
            if (aiItem) {
                if (aiItem.summary) {
                    aiOrders = aiItem.summary.total_orders;
                    aiRevenue = aiItem.summary.total_sales;
                } else {
                    aiItem.sales_detail.forEach((detail) => {
                        aiOrders += detail.order_quantity || 0;
                        aiRevenue += detail.total_price || 0;
                    });
                }
            }

            return {
                itemId: item.item_id,
                menuItem,
                categoryName: category?.name,
                orders,
                revenue,
                aiOrders,
                aiRevenue,
            };
        }).sort((a, b) => b.revenue - a.revenue);
    }, [salesData, menuItemsMap, cachedCategories]);

    // Performance chart data
    const performanceChartData = useMemo((): { month: string;[key: string]: string | number | undefined }[] => {
        if (!performanceData?.items) return [];

        const dataMap = new Map<string, { month: string;[key: string]: string | number | undefined }>();

        performanceData.items.forEach((item: ItemPerformanceItem) => {
            item.data.forEach((point) => {
                const key = `${point.year}-${String(point.month).padStart(2, "0")}`;
                if (!dataMap.has(key)) {
                    dataMap.set(key, {
                        month: new Date(point.year, point.month - 1).toLocaleDateString("en-US", {
                            month: "short",
                            year: "numeric",
                        }),
                    });
                }
                const entry = dataMap.get(key)!;
                entry[`revenue_${item.item_id}`] = point.total_price;
                entry[`orders_${item.item_id}`] = point.order_quantity;
            });
        });

        return Array.from(dataMap.values()).sort((a, b) => {
            const aMonth = a.month as string;
            const bMonth = b.month as string;
            return new Date(aMonth).getTime() - new Date(bMonth).getTime();
        });
    }, [performanceData]);

    // Items summary for performance table
    const itemsSummary = useMemo(() => {
        if (!performanceData?.items) return [];

        return performanceData.items
            .map((item) => {
                const totalRevenue = item.data.reduce((sum, p) => sum + p.total_price, 0);
                const totalOrders = item.data.reduce((sum, p) => sum + p.order_quantity, 0);
                const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

                return {
                    item_id: item.item_id,
                    totalRevenue,
                    totalOrders,
                    avgOrderValue,
                };
            })
            .sort((a, b) => b.totalRevenue - a.totalRevenue);
    }, [performanceData]);

    // Performance totals
    const performanceTotals = useMemo(() => {
        if (!itemsSummary.length) {
            return { totalRevenue: 0, totalOrders: 0 };
        }
        return {
            totalRevenue: itemsSummary.reduce((sum, item) => sum + item.totalRevenue, 0),
            totalOrders: itemsSummary.reduce((sum, item) => sum + item.totalOrders, 0),
        };
    }, [itemsSummary]);

    const averageOrderValue = useMemo(() => {
        if (performanceTotals.totalOrders === 0) {
            return 0;
        }
        return performanceTotals.totalRevenue / performanceTotals.totalOrders;
    }, [performanceTotals.totalOrders, performanceTotals.totalRevenue]);

    return (
        <div className="space-y-10">
            {/* ========== BRANCH SALES OVERVIEW SECTION ========== */}
            <section className="space-y-6">
                {/* Section Header */}
                <div className="flex justify-center items-center gap-3">
                    <div>
                        <h2 className="text-4xl font-bold text-gray-900 dark:text-white text-center">
                            {t("analytics.branchSalesOverview", "Branch Sales Overview")}
                        </h2>
                        <p className="text-sm text-center text-gray-500 dark:text-gray-400">
                            {t("analytics.branchSalesDescription", "Monitor your branch's overall sales performance")}
                        </p>
                    </div>
                </div>

                {/* Date Selection Card */}
                <Card className="!p-6 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 border border-gray-200 dark:border-gray-700 shadow-md hover:shadow-lg transition-shadow">
                    <div className="space-y-10">
                        {/* Header */}
                        <div className="flex items-center gap-3 pb-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                <FiCalendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                                    {t("analytics.selectDateRange", "Select Date Range")}
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {t("analytics.dateRangeDescription", "Choose dates to analyze your sales data")}
                                </p>
                            </div>
                        </div>

                        {/* Quick Presets */}
                        <div className="flex flex-wrap flex-col center text-center justify-center">
                            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide block mb-3">
                                {t("analytics.quickOptions", "Quick Options")}
                            </label>
                            <QuickDatePresets
                                activePreset={activePreset}
                                onPresetSelect={handlePresetSelect}
                                disabled={salesLoading}
                            />
                        </div>

                        {/* Date Range Picker */}
                        <div className="flex flex-wrap flex-col center text-center justify-center ">
                            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide block mb-3">
                                {t("analytics.customRange", "Custom Range")}
                            </label>
                            <DateRangePicker
                                startDate={branchStartDate}
                                endDate={branchEndDate}
                                onStartDateChange={handleBranchStartDateChange}
                                onEndDateChange={handleBranchEndDateChange}
                                maxDate={new Date()}
                                minDate={minDateLimit}
                                disabled={salesLoading}
                            />
                        </div>
                    </div>
                </Card>

                {/* Error State */}
                {salesError && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400"
                    >
                        {salesError}
                    </motion.div>
                )}

                {/* Stats Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatsCard
                        title={t("analytics.totalSales", "Total Sales")}
                        value={formatCurrencyValue(branchTotals.totalSales)}
                        icon={<FiDollarSign />}
                        gradient="from-emerald-500 to-emerald-600"
                        isLoading={salesLoading}
                        delay={0}
                    />
                    <StatsCard
                        title={t("analytics.totalOrders", "Total Orders")}
                        value={branchTotals.totalOrders.toLocaleString()}
                        icon={<FiShoppingCart />}
                        gradient="from-blue-500 to-blue-600"
                        isLoading={salesLoading}
                        delay={0.1}
                    />
                    <StatsCard
                        title={t("analytics.aiSales", "AI Sales")}
                        value={formatCurrencyValue(branchTotals.aiSales)}
                        subtitle={`${branchTotals.aiOrders.toLocaleString()} ${t("analytics.orders", "orders")}`}
                        icon={<FiCpu />}
                        gradient="from-purple-500 to-purple-600"
                        isLoading={salesLoading}
                        delay={0.2}
                    />
                    <StatsCard
                        title={t("analytics.regularSales", "Regular Sales")}
                        value={formatCurrencyValue(branchTotals.regularSales)}
                        subtitle={`${branchTotals.regularOrders.toLocaleString()} ${t("analytics.orders", "orders")}`}
                        icon={<FiPackage />}
                        gradient="from-orange-500 to-orange-600"
                        isLoading={salesLoading}
                        delay={0.3}
                    />
                </div>

                {/* Sales Chart */}
                <SalesChart
                    title={t("analytics.salesTrend", "Sales Trend")}
                    subtitle={t("analytics.dailySalesOrders", "Daily sales and orders overview")}
                    data={branchChartData}
                    chartType="bar"
                    isLoading={salesLoading}
                    height={350}
                    currencyCode={currencyCode}
                    currencySymbol={currencySymbol}
                    formatCurrency={formatCurrencyValue}
                />

                {/* Items Breakdown Table */}
                <ItemBreakdownTable
                    data={itemsBreakdownData}
                    isLoading={salesLoading}
                    title={t("analytics.itemsBreakdown", "Items Breakdown")}
                    formatCurrency={formatCurrencyValue}
                    branchId={branchId}
                />
            </section>

            {/* ========== ITEM PERFORMANCE COMPARISON SECTION ========== */}
            {/* Divider */}
            <div className="relative">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200 dark:border-gray-700" />
                </div>
                <div className="relative flex justify-center">
                    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 rounded-full">
                        <FiTrendingUp className="w-5 h-5 text-gray-400" />
                    </div>
                </div>
            </div>

            <section className="space-y-6">
                {/* Section Header */}
                <div className="flex items-center gap-3">
                    <div className="w-1.5 h-10 bg-gradient-to-b from-emerald-500 to-emerald-600 rounded-full" />
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                            {t("analytics.itemPerformanceComparison", "Item Performance Comparison")}
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t("analytics.itemPerformanceDescription", "Compare performance metrics across selected items")}
                        </p>
                    </div>
                </div>

                {/* Item Selection - Always Visible */}
                <Card className="!p-5">
                    <div className="space-y-6">
                        {/* Item Selector */}
                        <ItemSelector
                            selectedItemIds={selectedItemIds}
                            menuItems={cachedMenuItems}
                            categories={cachedCategories}
                            maxItems={5}
                            onItemSelect={handleItemSelect}
                            onItemRemove={handleItemRemove}
                            disabled={performanceLoading}
                            branchId={branchId}
                        />

                        {selectedItemIds.length > 0 && (
                            <>
                                {/* Divider */}
                                <div className="border-t border-gray-100 dark:border-gray-700" />

                                {/* Date Range */}
                                <div>
                                    <div className="flex items-center gap-2 mb-3">
                                        <FiCalendar className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                                            {t("analytics.performanceDateRange", "Performance Date Range")}
                                        </span>
                                    </div>
                                    <DateRangePicker
                                        startDate={perfStartDate}
                                        endDate={perfEndDate}
                                        onStartDateChange={(date) => {
                                            if (date) {
                                                const minDate = convertBranchStartDateToDate(contextBranchStartDate);
                                                if (minDate && date < minDate) {
                                                    console.warn(`Cannot select a date before branch creation date: ${minDate.toLocaleDateString()}`);
                                                    return;
                                                }
                                                setPerfStartDate(date);
                                            }
                                        }}
                                        onEndDateChange={(date) => {
                                            if (date) {
                                                const minDate = convertBranchStartDateToDate(contextBranchStartDate);
                                                if (minDate && date < minDate) {
                                                    console.warn(`Cannot select a date before branch creation date: ${minDate.toLocaleDateString()}`);
                                                    return;
                                                }
                                                setPerfEndDate(date);
                                            }
                                        }}
                                        maxDate={new Date()}
                                        minDate={minDateLimit}
                                        disabled={performanceLoading}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </Card>

                {/* Error State */}
                {selectedItemIds.length > 0 && performanceError && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400"
                    >
                        {performanceError}
                    </motion.div>
                )}

                {/* Stats Cards */}
                {selectedItemIds.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <StatsCard
                            title={t("analytics.totalRevenue", "Total Revenue")}
                            value={formatCurrencyValue(performanceTotals.totalRevenue)}
                            icon={<FiDollarSign />}
                            gradient="from-emerald-500 to-emerald-600"
                            isLoading={performanceLoading}
                            delay={0}
                        />
                        <StatsCard
                            title={t("analytics.totalOrders", "Total Orders")}
                            value={performanceTotals.totalOrders.toLocaleString()}
                            icon={<FiShoppingCart />}
                            gradient="from-blue-500 to-blue-600"
                            isLoading={performanceLoading}
                            delay={0.1}
                        />
                        <StatsCard
                            title={t("analytics.avgOrderValue", "Avg Order Value")}
                            value={formatCurrencyValue(averageOrderValue)}
                            icon={<FiTrendingUp />}
                            gradient="from-purple-500 to-purple-600"
                            isLoading={performanceLoading}
                            delay={0.2}
                        />
                    </div>
                )}

                {/* Performance Charts */}
                {selectedItemIds.length > 0 && (
                    <PerformanceComparisonChart
                        chartData={performanceChartData}
                        itemsSummary={itemsSummary}
                        menuItemsMap={menuItemsMap}
                        isLoading={performanceLoading}
                        formatCurrency={formatCurrencyValue}
                        branchId={branchId}
                    />
                )}

                {/* Items Performance Table */}
                {selectedItemIds.length > 0 && (
                    <ItemPerformanceTable
                        itemsSummary={itemsSummary}
                        menuItemsMap={menuItemsMap}
                        categories={cachedCategories}
                        isLoading={performanceLoading}
                        formatCurrency={formatCurrencyValue}
                        branchId={branchId}
                    />
                )}
            </section>
        </div>
    );
};

export default SalesAnalyticsTab;
